import Close from '@mui/icons-material/Close'
import History from '@mui/icons-material/History'
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useSelector } from 'react-redux'
import { selectPrescriptionViewSettingsData } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { allAvailableEventTypes } from 'src/consumer/constants'
import LSPastVisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/LSPastVisitReportsComponent'
import { useBodyScrollLock } from 'src/hooks/useBodyScrollLock'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'

/**
 * Patient History drawer for manage-my-bookings cards — the SAME patient-history experience the
 * report making pages embed (LSPastVisitReportsComponent), not the legacy past-therapy dialog.
 * Role mirrors the making pages / live-session split: doctor consultations get the doctor view
 * (Consultation Summary + Therapy Summary), everything else the therapist view.
 */
export default function PatientHistoryDrawer({ open, onClose, bookedEvent }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  useBodyScrollLock(open, 1300)

  const prescriptionViewSettings = useSelector(selectPrescriptionViewSettingsData)?.data

  const roleForHistory =
    bookedEvent?.eventType === allAvailableEventTypes.consultation &&
    getAllRolesInBoolean().isDoctor
      ? 'doctor'
      : 'therapist'

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '950px',
          maxWidth: isMobile ? '100%' : '92vw',
          height: isMobile ? '90vh' : '100%',
          maxHeight: isMobile ? '90vh' : '100%',
        },
      }}
      ModalProps={{
        disableScrollLock: true, // handled by useBodyScrollLock
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#ffffff' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: 'rgba(0, 0, 0, 0.87)' }}>
            Patient History
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2, backgroundColor: '#f9fafb' }}>
          {open && bookedEvent?.patientId ? (
            <LSPastVisitReportsComponent
              patientId={bookedEvent.patientId}
              referenceBookedEventId={bookedEvent.id}
              role={roleForHistory}
              clinicId={bookedEvent.clinicId}
              prescriptionViewSettings={prescriptionViewSettings}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2,
              }}
            >
              <History sx={{ fontSize: 64, color: '#d1d5db' }} />
              <Typography color="text.secondary">No patient history available</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
